import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Typography,
} from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import AppButton from "../../ui/AppButton";
import { getToken } from "../../config/msalConfig";

const BACKEND = process.env.REACT_APP_BACKEND_URI;

const EVENT_META = {
  start:   { icon: InfoOutlinedIcon,           color: "#3b82f6" },
  info:    { icon: InfoOutlinedIcon,           color: "#64748b" },
  created: { icon: CheckCircleOutlineIcon,     color: "#16a34a" },
  updated: { icon: SyncAltIcon,               color: "#2563eb" },
  skipped: { icon: InfoOutlinedIcon,           color: "#94a3b8" },
  warning: { icon: WarningAmberOutlinedIcon,   color: "#f97316" },
  error:   { icon: ErrorOutlineIcon,           color: "#dc2626" },
};

function LogLine({ event }) {
  const meta = EVENT_META[event.type] || EVENT_META.info;
  const Icon = meta.icon;
  const text = event.name
    ? `${event.type === "created" ? "Created" : event.type === "updated" ? "Updated" : "Skipped"}: ${event.name}`
    : event.message || "";
  return (
    <Box display="flex" alignItems="flex-start" gap={0.8} py={0.3}>
      <Icon sx={{ fontSize: 14, color: meta.color, flexShrink: 0, mt: "2px" }} />
      <Typography fontSize={12.5} sx={{ color: "text.primary", lineHeight: 1.5 }}>{text}</Typography>
    </Box>
  );
}

async function downloadTemplate() {
  const token = await getToken();
  const res = await fetch(`${BACKEND}/api/projects/bulk/template`, {
    headers: { "oidc-token": token },
  });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "project_import_template.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

function ProjectImportDialog({ open, onClose, onDone }) {
  const fileRef = useRef(null);
  const logEndRef = useRef(null);
  const abortRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [lines, setLines] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) { setSelectedFile(file); setLines([]); setIsDone(false); setSummary(null); setError(""); }
  };

  const handleImport = async () => {
    if (!selectedFile || isRunning) return;
    setLines([]);
    setIsDone(false);
    setSummary(null);
    setError("");
    setIsRunning(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = await getToken();
      const form = new FormData();
      form.append("file", selectedFile);
      const res = await fetch(
        `${BACKEND}/api/projects/bulk/import/stream?update_if_exists=${updateExisting}`,
        { method: "POST", headers: { "oidc-token": token }, body: form, signal: controller.signal }
      );
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Import failed"); }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() ?? "";
        for (const chunk of chunks) {
          for (const line of chunk.split("\n")) {
            if (!line.startsWith("data: ")) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === "done") {
                setSummary(event);
                setIsDone(true);
                onDone?.();
              } else {
                setLines((prev) => [...prev, event]);
              }
            } catch { /* ignore malformed */ }
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") setError(err.message || "Import failed");
    } finally {
      setIsRunning(false);
    }
  };

  const handleClose = () => {
    abortRef.current?.abort();
    setSelectedFile(null);
    setLines([]);
    setIsDone(false);
    setSummary(null);
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={isDone ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Import Projects from Excel</DialogTitle>

      {isRunning && <LinearProgress />}

      <DialogContent sx={{ pt: 1.5 }}>
        {/* Step 1 — download template */}
        <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 2 }}>
          <Typography fontSize={13} fontWeight={600} mb={0.5}>Step 1 — Download the template</Typography>
          <Typography fontSize={12} color="text.secondary" mb={1.5}>
            Fill in project details. Required: <strong>Project Name</strong>. Use employee emails for manager and assignees.
          </Typography>
          <AppButton
            variant="outlined"
            size="small"
            startIcon={<UploadFileOutlinedIcon />}
            loading={isDownloading}
            onClick={async () => {
              setIsDownloading(true);
              try { await downloadTemplate(); } finally { setIsDownloading(false); }
            }}
          >
            Download Template (.xlsx)
          </AppButton>
        </Box>

        {/* Step 2 — upload filled file */}
        <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, mb: lines.length ? 2 : 0 }}>
          <Typography fontSize={13} fontWeight={600} mb={1.5}>Step 2 — Upload filled file</Typography>

          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleFileChange} />

          <Box display="flex" alignItems="center" gap={1.5}>
            <AppButton variant="outlined" size="small" onClick={() => fileRef.current?.click()} disabled={isRunning}>
              Choose File
            </AppButton>
            <Typography fontSize={13} color={selectedFile ? "text.primary" : "text.secondary"}>
              {selectedFile ? selectedFile.name : "No file chosen"}
            </Typography>
          </Box>

          <FormControlLabel
            sx={{ mt: 1, ml: 0.2 }}
            control={<Checkbox size="small" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} disabled={isRunning} />}
            label={<Typography fontSize={13}>Update existing projects with data from file</Typography>}
          />
        </Box>

        {/* Live log */}
        {(lines.length > 0 || isRunning) && (
          <Box
            sx={{
              bgcolor: "background.default",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 1.5,
              minHeight: 120,
              maxHeight: 300,
              overflowY: "auto",
            }}
          >
            {lines.length === 0 && isRunning && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={13} />
                <Typography fontSize={12} color="text.secondary">Starting…</Typography>
              </Box>
            )}
            {lines.map((event, idx) => <LogLine key={idx} event={event} />)}
            <div ref={logEndRef} />
          </Box>
        )}

        {isDone && summary && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography fontSize={13} fontWeight={700} mb={1}>Import complete</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip label={`${summary.total} created`} size="small" sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 600 }} />
              {summary.skipped?.length > 0 && (
                <Chip label={`${summary.skipped.length} skipped/updated`} size="small" sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600 }} />
              )}
              {summary.errors?.length > 0 && (
                <Chip label={`${summary.errors.length} errors`} size="small" sx={{ bgcolor: "#fef2f2", color: "#dc2626", fontWeight: 600 }} />
              )}
            </Box>
          </>
        )}

        {error && <Typography fontSize={13} color="error" mt={1.5}>{error}</Typography>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <AppButton variant="outlined" onClick={handleClose} disabled={isRunning && !isDone}>
          {isDone ? "Close" : "Cancel"}
        </AppButton>
        {!isDone && (
          <AppButton variant="contained" onClick={handleImport} loading={isRunning} disabled={!selectedFile}>
            Import
          </AppButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ProjectImportDialog;
